import { json } from '@remix-run/node';
import type { LoaderFunctionArgs } from '@remix-run/node';
import type { MetaFunction } from '@remix-run/react';
import { useLoaderData } from '@remix-run/react';
import axiosInstance from '~/api';
import { SITEMAP_DATA } from '~/api/apiUrlsV2';
import { useTranslation } from 'react-i18next';
import Footer from '~/components/layouts/footer/Footer';
import Navbar from '~/components/layouts/navigation/Navbar';
import i18next from '~/services/i18next.server';
import { SitemapData } from './sitemap[.]xml';

export const loader = async ({ params }: LoaderFunctionArgs) => {
  try {
    const { data } = await axiosInstance.get<{
      lastVersionUpdate: string;
      sitemapData: SitemapData;
    }>(SITEMAP_DATA);

    const t = await i18next.getFixedT(params.lang || 'fr');
    const translations = {
      title: t('sitemap.title'),
      description: t('metaTags.SeoTags.description'),
    };

    return json({
      ...data,
      translations: translations,
      baseUrl: import.meta.env.VITE_API_BASE_URL,
      locale: params.lang || 'fr',
    });
  } catch (error: any) {
    if (error.response)
      throw new Response(null, {
        status: error.response.status,
        statusText: error.response.statusText,
      });
    else throw new Error(error);
  }
};

export const meta: MetaFunction = ({ data }: any) => {
  if (data === undefined) return [];

  const lang = data.locale;
  const title = data.translations.title;
  const description = data.translations.description;
  const canonicalUrl = `${data.baseUrl}/${lang}/sitemap`;

  return [
    { title: title },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    { tagName: 'link', rel: 'alternate', hrefLang: 'fr', href: `${data.baseUrl}/fr/sitemap` },
    { tagName: 'link', rel: 'alternate', hrefLang: 'en', href: `${data.baseUrl}/en/sitemap` },
    {
      tagName: 'link',
      rel: 'alternate',
      hrefLang: 'x-default',
      href: `${data.baseUrl}/en/sitemap`,
    },
    {
      tagName: 'link',
      rel: 'icon',
      sizes: '32x32',
      href: `${data.baseUrl}/favicon-32x32.png`,
    },
    { tagName: 'link', rel: 'icon', sizes: '16x16', href: `${data.baseUrl}/favicon-16x16.png` },
    {
      tagName: 'link',
      rel: 'mask-icon',
      href: `${data.baseUrl}/safari-pinned-tab.svg`,
      color: '#1c1a1f',
    },
    { tagName: 'link', rel: 'manifest', href: `${data.baseUrl}/site.webmanifest` },
    { name: 'google', content: 'notranslate' },
    { name: 'msapplication-TileColor', content: '#1c1a1f' },
    { name: 'og:site_name', content: 'Fiveoffices' },
    { name: 'og:type', content: 'website' },
    { name: 'og:title', content: title },
    { name: 'twitter:title', content: title },
    { name: 'description', content: description },
    { name: 'og:description', content: description },
    { name: 'twitter:description', content: description },
    { name: 'twitter:card', content: description },
    { name: 'locale', content: lang },
    { name: 'og:locale', content: lang },
  ];
};

type SitemapUrl = {
  path: string;
  title: string;
};

export default function Sitemap() {
  const { locale, sitemapData } = useLoaderData<typeof loader>();
  const { t } = useTranslation();

  const navbarConfig = {
    showDesktopSearch: true,
    showMobileSearch: false,
    showMobileHeader: true,
    showBottomNav: true,
  };

  const slugsAndTitles = [
    // { slug: 'offices', title: t('sitemap.offices.title') },
    { slug: 'coworking', title: t('sitemap.coworking.title') },
    { slug: 'offices', title: t('sitemap.officesForRent.title') },
    { slug: 'flexible_offices', title: t('sitemap.flexibleOffices.title') },
  ];

  return (
    <>
      <Navbar configuration={navbarConfig} />

      <div className="container fo-sitemap fo-typography">
        {slugsAndTitles.map(({ slug, title }) => (
          <div key={slug}>
            <h2 className="fo-display-2 my-8">{title}</h2>
            <div className="fo-sitemap__content">
              {sitemapData['seoLocations'][locale]
                .filter((page: { slug: string }) => page.slug === slug)
                .sort((page1: SitemapUrl, page2: SitemapUrl) => (page1.title < page2.title ? -1 : 1))
                .map((seoPage: { title: string; path: string }) => (
                  <div
                    key={seoPage.title}
                    className="fo-sitemap-link text-truncate"
                  >
                    <a
                      className="text-1 -medium text-decoration-underline link-underline-primary link-offset-2 link-underline-opacity-50 link-underline-opacity-100-hover"
                      href={seoPage.path}
                    >
                      {seoPage.title}
                    </a>
                  </div>
                ))}
            </div>
          </div>
        ))}
        <div>
          <h2 className="fo-display-2 my-8">{t('sitemap.blog.title')}</h2>
          <div className="fo-sitemap__content fo-sitemap__content--oneline">
            {sitemapData['blogEntries'][locale].map((seoPage: { title: string; url: string }) => (
              <div
                key={seoPage.title}
                className="fo-sitemap-link text-truncate"
              >
                <a
                  className="text-1 -medium text-decoration-underline link-underline-primary link-offset-2 link-underline-opacity-50 link-underline-opacity-100-hover"
                  href={seoPage.url}
                >
                  {seoPage.title}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer showAvailableOffices={true} />
    </>
  );
}
